import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield } from "lucide-react";

const AdminGate = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => { refs[0].current?.focus(); }, []); // eslint-disable-line

  const setDigit = (i, v) => {
    const only = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = only;
    setDigits(next);
    if (only && i < 3) refs[i + 1].current?.focus();
    if (next.every((d) => d !== "")) submit(next.join(""));
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs[i - 1].current?.focus();
  };

  const submit = async (pin) => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/gate", { slug, pin });
      localStorage.setItem("rieave.gate.token", data.access_token);
      toast.success("Gate unlocked");
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error("Incorrect PIN");
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden"
      style={{ background: "radial-gradient(1200px 700px at 50% 40%, #1f1520 0%, #0F0812 60%, #000 100%)" }}
      data-testid="admin-gate-page"
    >
      {/* decorative glows */}
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #C9789A, transparent 70%)" }} />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #9B8FBF, transparent 70%)" }} />

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-[28px] p-[1.5px]"
        style={{ background: "linear-gradient(135deg, rgba(201,120,154,0.75), rgba(212,184,150,0.35), rgba(155,143,191,0.75))" }}
      >
        <div className="rounded-[27px] bg-[#120A14]/90 backdrop-blur p-9 text-cream">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6" style={{ background: "linear-gradient(135deg, #C9789A, #9B8FBF)" }}>
            <Shield size={18} className="text-cream" />
          </div>
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-rose/80 mb-2">Private · rieave atelier</p>
          <h1 className="font-hero text-3xl md:text-4xl text-cream mb-3">
            Enter your <span className="serif-italic" style={{ color: "#E8B8CE" }}>atelier PIN</span>
          </h1>
          <p className="text-cream/60 text-sm mb-8">
            Only those with the key may proceed. Enter the 4-digit code to unlock the admin entrance.
          </p>

          <div className="flex items-center justify-between gap-3 mb-6">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={refs[i]}
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(i, e)}
                disabled={loading}
                className="w-full aspect-square text-center font-hero text-3xl text-cream bg-white/5 border border-white/10 rounded-2xl focus:outline-none focus:border-rose focus:ring-2 focus:ring-rose/30 transition"
                data-testid={`pin-digit-${i}`}
              />
            ))}
          </div>

          <p className="text-center font-label text-[10px] uppercase tracking-[0.3em] text-cream/65">
            {loading ? "Verifying…" : "Auto-submits when all digits are entered"}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminGate;
